import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

type ToastKind = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
}

export interface ToastApi {
  /** Show a toast of the given kind (defaults to success). Returns nothing. */
  show: (message: string, kind?: ToastKind) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

// Auto-dismiss after this long; errors linger a touch longer so they're read.
const TIMEOUT: Record<ToastKind, number> = { success: 4000, info: 4000, error: 6000 };

// A colored accent bar + icon per kind, on the standard surface card.
const STYLES: Record<ToastKind, { bar: string; icon: string; glyph: string }> = {
  success: { bar: 'bg-positive', icon: 'text-positive', glyph: '✓' },
  error: { bar: 'bg-negative', icon: 'text-negative', glyph: '!' },
  info: { bar: 'bg-info', icon: 'text-info', glyph: 'i' },
};

// Global, app-wide toast notifications. Lives near the app root so a toast
// survives the unmount of whatever triggered it (e.g. a modal that closes on
// success). Use via the `useToast()` hook.
export function ToastProvider({ children }: PropsWithChildren): JSX.Element {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const remove = useCallback((id: number): void => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (message: string, kind: ToastKind = 'success'): void => {
      const id = (idRef.current += 1);
      setToasts((current) => [...current, { id, message, kind }]);
      window.setTimeout(() => remove(id), TIMEOUT[kind]);
    },
    [remove],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (m) => show(m, 'success'),
      error: (m) => show(m, 'error'),
      info: (m) => show(m, 'info'),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 max-w-[calc(100vw-2rem)]"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((t) => {
          const s = STYLES[t.kind];
          return (
            <div
              key={t.id}
              role="status"
              className="kc-toast flex items-start gap-3 w-80 max-w-full bg-surface border border-rule rounded-lg shadow-xl overflow-hidden"
            >
              <div className={`w-1 self-stretch shrink-0 ${s.bar}`} />
              <div
                className={`mt-3 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${s.icon}`}
                aria-hidden="true"
              >
                {s.glyph}
              </div>
              <p className="flex-1 py-3 text-sm text-ink pr-1">{t.message}</p>
              <button
                type="button"
                onClick={() => remove(t.id)}
                className="text-ink-3 hover:text-ink text-lg leading-none px-3 py-3"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
